import { Component, ViewChild, ElementRef, Inject, DestroyRef, inject } from '@angular/core';
import { BaseModalComponent } from './base-modal.component';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import moment from 'moment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AdminService, Costcenter } from '../../../core/services/admin.service';
import { formatCostcenterLabel } from '../../utils/costcenter.utils';

export interface InvoiceDetail {
  name: string;
  external_project_name: string;
  external_activity_name: string;
  sum: number;
  price: number;
  pris?: number;
  activity_costcenter: string;
  comment: string;
  costcenter?: string;
  project_name?: string;
  activity_name?: string;
}

export interface InvoiceStatement {
  invoice_number?: string;
  invoice_date?: string;
  inputdate?: string;
  extprojectid: string;
  invoice_comment: string;
  details: InvoiceDetail[];
  pris?: number;
  org_statement?: any;
}

export interface InvoiceData {
  invoice_number: string;
  invoice_date: string;
  statement: InvoiceStatement;
  amount?: number;
  // other fields...
}

export interface EditInvoiceDialogData {
  headerText: string;
  closeButtonText: string;
  actionButtonText: string;
  inv: InvoiceData;
}

@Component({
  selector: 'app-edit-invoice-modal',
  standalone: false,
  template: require('./edit-invoice-modal.component.html').default,
  styles: [
    require('!!raw-loader!sass-loader!./base-modal.component.scss').default,
    require('!!raw-loader!sass-loader!./edit-invoice-modal.component.scss').default,
  ],
  animations: [BaseModalComponent.modalAnimations[0]],
})
export class EditInvoiceModalComponent extends BaseModalComponent {
  headerText = 'Editera faktura';
  closeButtonText = 'Avbryt';
  actionButtonText = 'OK';

  inv!: InvoiceData;
  costcenters: Costcenter[] = [];

  private destroyRef = inject(DestroyRef);

  constructor(@Inject(AdminService) private adminService: AdminService) {
    super();
  }

  configure(data: EditInvoiceDialogData): void {
    this.headerText = data.headerText;
    this.closeButtonText = data.closeButtonText;
    this.actionButtonText = data.actionButtonText;

    // Deep copy to avoid modifying original until save
    this.inv = JSON.parse(JSON.stringify(data.inv));

    // Parse statement if it is a string (legacy data format)
    if (typeof this.inv.statement === 'string') {
      try {
        this.inv.statement = JSON.parse(this.inv.statement as any);
        if (!this.inv.invoice_date) {
          this.inv.invoice_date = this.inv.statement.invoice_date || this.inv.statement.inputdate || '';
        }
      } catch (e) {
        console.error('Failed to parse invoice statement', e);
        this.inv.statement = { details: [], extprojectid: '', invoice_comment: '' } as any;
      }
    }

    // Ensure details array exists
    if (!this.inv.statement.details) {
      this.inv.statement.details = [];
    }

    // Ensure calculations are correct initially
    this.calculateTotal();

    if (this.costcenters.length === 0) {
      this.adminService.getCostcenters().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (rows) => {
          this.costcenters = rows;
        },
        error: (err) => console.error('Error loading costcenters for invoice modal', err),
      });
    }

  }

  calculateTotal(): void {
    if (this.inv && this.inv.statement && this.inv.statement.details) {
      this.inv.statement.pris = 0;
      this.inv.statement.details.forEach((det) => {
        det.pris = (det.sum || 0) * (det.price || 0);
        this.inv.statement.pris! += det.pris;
      });
      this.inv.amount = this.inv.statement.pris;
    }
  }

  addDetail(index: number): void {
    const newDetail: InvoiceDetail = {
      name: '',
      external_project_name: '',
      external_activity_name: '',
      sum: 0,
      price: 0,
      activity_costcenter: '',
      comment: '',
    };
    this.inv.statement.details.splice(index, 0, newDetail);
  }

  removeDetail(index: number): void {
    if (this.inv.statement.details.length > 1) {
      this.inv.statement.details.splice(index, 1);
      this.calculateTotal();
    }
  }

  save(): void {
    this.calculateTotal();
    this.confirm(this.inv);
  }

  getInvoiceDate(): moment.Moment {
    if (this.inv && this.inv.invoice_date) {
      return moment(this.inv.invoice_date);
    }
    return moment();
  }

  onInvoiceDateChange(date: moment.Moment): void {
    if (date && this.inv) {
      this.inv.invoice_date = date.format('YYYY-MM-DD');
    }
  }

  protected readonly formatCostcenterLabel = formatCostcenterLabel;
}
